import type { Website } from 'types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from 'helpers/cn';
import type { TrackingMethodChoice } from '../tracking-methods';
import { useTrackingMethodChoice } from '../use-tracking-method-choice';
import { ServerTrackingConfirm } from '../ServerTrackingConfirm';

type WebsiteSettingsSave = {
  defaultCurrency: string;
  trackingMethod: string | null;
  conversionEvent: string | null;
};

type WebsiteSettingsProps = {
  website: Website;
  /** Event names mapped primary-eligible below. The default has to be one of them. */
  primaryOptions: string[];
  onSave: (settings: WebsiteSettingsSave) => Promise<void>;
};

/** Chosen when a website has no default; the pipeline measures 'purchase' regardless. */
const NO_DEFAULT = '__none__';

const SELECT_TRIGGER = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

type TrackingMethod = TrackingMethodChoice;

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'SEK', label: 'SEK — Swedish Krona' },
  { value: 'NOK', label: 'NOK — Norwegian Krone' },
  { value: 'DKK', label: 'DKK — Danish Krone' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
];

export const WebsiteSettings = ({ website, primaryOptions, onSave }: WebsiteSettingsProps) => {
  const originalCurrency = website.defaultCurrency || 'USD';
  const originalMethod: TrackingMethod = website.trackingMethod ?? 'none';
  const originalConversionEvent = website.conversionEvent ?? NO_DEFAULT;

  const [currency, setCurrency] = useState(originalCurrency);
  const [trackingMethod, setTrackingMethod] = useState<TrackingMethod>(originalMethod);
  const [conversionEvent, setConversionEvent] = useState(originalConversionEvent);
  const choice = useTrackingMethodChoice({ website, enabled: true, setTrackingMethod });

  const hasChanges =
    currency !== originalCurrency || trackingMethod !== originalMethod || conversionEvent !== originalConversionEvent;

  const handleSave = async () => {
    await onSave({
      defaultCurrency: currency,
      trackingMethod: trackingMethod === 'none' ? null : trackingMethod,
      conversionEvent: conversionEvent === NO_DEFAULT ? null : conversionEvent,
    });
  };

  /**
   * A value saved before this became a select — or one whose event has since been moved
   * out of the primary pool — still has to be selectable, or opening this card and saving
   * an unrelated field would silently discard it.
   */
  const options =
    conversionEvent !== NO_DEFAULT && !primaryOptions.includes(conversionEvent)
      ? [conversionEvent, ...primaryOptions]
      : primaryOptions;

  return (
    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <h2 className="text-foreground m-0 text-body font-medium">Settings</h2>
          <p className="supporting m-0 mt-0.5">Configure implementation settings for this website.</p>
        </div>
      </div>

      <div className="space-y-5 p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <span className="micro-label block">Default currency</span>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className={cn(SELECT_TRIGGER, 'w-full max-w-[220px]')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <span className="micro-label block">Default primary objective</span>
            <Select value={conversionEvent} onValueChange={setConversionEvent}>
              <SelectTrigger className={cn(SELECT_TRIGGER, 'w-full max-w-[260px]')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEFAULT}>Not set — assume purchase</SelectItem>
                {options.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="supporting m-0">
              {primaryOptions.length === 0
                ? 'Map an event as a primary objective below to choose one here.'
                : 'Used for experiments that do not choose their own objective.'}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div>
            <span className="micro-label block">Tracking method</span>
            <p className="supporting m-0 mt-1">Choose how you want to track conversions and events for this website.</p>
          </div>

          <RadioGroup
            value={trackingMethod}
            onValueChange={(v) => choice.choose(v as TrackingMethod)}
            // One column on a phone: each card carries a description, and 160px is not
            // enough width for a sentence that has to be read before choosing.
            className="grid gap-2 sm:grid-cols-2"
          >
            {choice.options.map((method) => (
              <label
                key={method.value}
                className={cn(
                  'rounded-card flex items-start gap-3 border p-3 transition-colors duration-150',
                  method.readOnly ? 'cursor-default' : 'cursor-pointer',
                  trackingMethod === method.value
                    ? 'bg-sel-bg border-sel-line'
                    : 'border-line bg-panel2 hover:bg-hover',
                )}
              >
                <RadioGroupItem value={method.value} disabled={method.readOnly} className="mt-0.5" />
                <div>
                  <div className="text-foreground text-meta font-medium">{method.label}</div>
                  <div className="supporting">{method.description}</div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>
      </div>

      {hasChanges && (
        <div className="border-line bg-panel2 rounded-b-panel flex items-center justify-end border-t px-4 py-3">
          <Button onClick={handleSave} className="h-9 rounded-lg">
            Save changes
          </Button>
        </div>
      )}
      <ServerTrackingConfirm choice={choice} />
    </section>
  );
};
